// Gemini AI Integration for Support Ticket Management
// This will be expanded with actual Gemini API integration

export class GeminiAIService {
  constructor(apiKey = null) {
    this.apiKey = apiKey;
    this.baseUrl = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent';
  }

  // Analyze support ticket content for categorization
  async analyzeTicket(title, description) {
    try {
      // This would make an actual API call to Gemini
      // For now, returning enhanced keyword analysis
      const analysis = {
        category: this.autoCategorize(title, description),
        priority: this.autoPrioritize(title, description),
        sentiment: this.analyzeSentiment(title, description),
        suggestedSolutions: this.generateSolutions(title, description),
        keywords: this.extractKeywords(title, description),
        urgency: this.calculateUrgency(title, description)
      };
      
      return analysis;
    } catch (error) {
      console.error('Gemini AI analysis error:', error);
      return this.getFallbackAnalysis(title, description);
    }
  }

  // Auto-categorize ticket based on content
  autoCategorize(title, description) {
    const text = `${title} ${description}`.toLowerCase();
    
    // Enhanced categorization logic
    if (text.includes('login') || text.includes('password') || text.includes('account') || text.includes('sign in')) {
      return 'account';
    }
    if (text.includes('team') || text.includes('member') || text.includes('captain') || text.includes('join team')) {
      return 'team';
    }
    if (text.includes('game') || text.includes('trivia') || text.includes('question') || text.includes('play')) {
      return 'game';
    }
    if (text.includes('venue') || text.includes('location') || text.includes('place') || text.includes('where')) {
      return 'venue';
    }
    if (text.includes('payment') || text.includes('billing') || text.includes('money') || text.includes('cost')) {
      return 'payment';
    }
    if (text.includes('error') || text.includes('bug') || text.includes('broken') || text.includes('not working')) {
      return 'technical';
    }
    if (text.includes('feature') || text.includes('request') || text.includes('suggestion') || text.includes('add')) {
      return 'feature';
    }
    
    return 'other';
  }

  // Auto-prioritize ticket based on content and keywords
  autoPrioritize(title, description) {
    const text = `${title} ${description}`.toLowerCase();
    
    // Urgent indicators
    if (text.includes('urgent') || text.includes('emergency') || text.includes('broken') || 
        text.includes('not working') || text.includes('blocked') || text.includes('cannot access')) {
      return 'urgent';
    }
    
    // High priority indicators
    if (text.includes('important') || text.includes('blocked') || text.includes('failed') || 
        text.includes('error') || text.includes('bug') || text.includes('crash')) {
      return 'high';
    }
    
    // Low priority indicators
    if (text.includes('suggestion') || text.includes('feature') || text.includes('improvement') || 
        text.includes('nice to have') || text.includes('when possible')) {
      return 'low';
    }
    
    return 'medium';
  }

  // Analyze sentiment of the ticket
  analyzeSentiment(title, description) {
    const text = `${title} ${description}`.toLowerCase();
    
    const positiveWords = ['great', 'awesome', 'love', 'excellent', 'amazing', 'perfect'];
    const negativeWords = ['terrible', 'awful', 'hate', 'broken', 'frustrated', 'angry', 'disappointed'];
    const urgentWords = ['urgent', 'emergency', 'broken', 'not working', 'blocked'];
    
    let sentiment = 'neutral';
    let urgency = 'normal';
    
    if (urgentWords.some(word => text.includes(word))) {
      urgency = 'high';
    }
    
    const positiveCount = positiveWords.filter(word => text.includes(word)).length;
    const negativeCount = negativeWords.filter(word => text.includes(word)).length;
    
    if (negativeCount > positiveCount) {
      sentiment = 'negative';
    } else if (positiveCount > negativeCount) {
      sentiment = 'positive';
    }
    
    return { sentiment, urgency };
  }

  // Generate suggested solutions based on category and content
  generateSolutions(title, description) {
    const category = this.autoCategorize(title, description);
    const priority = this.autoPrioritize(title, description);
    
    const solutions = {
      account: [
        'Try signing out and signing back in',
        'Check if your email address is correct',
        'Reset your password using the "Forgot Password" link',
        'Clear your browser cache and cookies',
        'Try using a different browser'
      ],
      team: [
        'Check team member permissions in team settings',
        'Verify team captain status and permissions',
        'Review team member limits (max 10 members)',
        'Check if team is full or has pending invitations',
        'Contact team captain for member management'
      ],
      game: [
        'Refresh the game page and try again',
        'Check your internet connection',
        'Try using a different browser or device',
        'Clear browser cache and cookies',
        'Check if the game is still available'
      ],
      venue: [
        'Verify venue information and address',
        'Check venue availability and hours',
        'Contact venue directly for confirmation',
        'Check if venue is still participating',
        'Look for alternative venues nearby'
      ],
      payment: [
        'Check your payment method details',
        'Verify billing information is correct',
        'Contact your payment provider',
        'Check for any pending charges',
        'Try a different payment method'
      ],
      technical: [
        'Clear browser cache and cookies',
        'Try using a different browser',
        'Check your internet connection',
        'Try using a different device',
        'Check system requirements and updates'
      ],
      feature: [
        'Feature request has been noted',
        'Will be reviewed by our development team',
        'Check our roadmap for similar features',
        'Consider using existing alternatives',
        'We\'ll notify you when this feature is available'
      ],
      other: [
        'Please provide more specific details',
        'Check our help documentation',
        'Contact support directly for assistance',
        'Try the troubleshooting guide',
        'We\'ll investigate this issue further'
      ]
    };
    
    // Add priority-specific solutions
    if (priority === 'urgent') {
      solutions[category].unshift('This is marked as urgent - we\'ll respond immediately');
    }
    
    return solutions[category] || solutions.other;
  }

  // Extract key terms from the ticket
  extractKeywords(title, description) {
    const text = `${title} ${description}`.toLowerCase();
    const words = text.split(/\s+/);
    const stopWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'can', 'this', 'that', 'these', 'those', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them'];
    
    const keywords = words
      .filter(word => word.length > 3 && !stopWords.includes(word))
      .filter(word => /^[a-zA-Z]+$/.test(word))
      .slice(0, 10);
    
    return [...new Set(keywords)];
  }

  // Calculate urgency score
  calculateUrgency(title, description) {
    const text = `${title} ${description}`.toLowerCase();
    let score = 0;
    
    // Urgency indicators
    if (text.includes('urgent')) score += 10;
    if (text.includes('emergency')) score += 10;
    if (text.includes('broken')) score += 8;
    if (text.includes('not working')) score += 7;
    if (text.includes('blocked')) score += 6;
    if (text.includes('cannot')) score += 5;
    if (text.includes('failed')) score += 5;
    if (text.includes('error')) score += 4;
    if (text.includes('bug')) score += 4;
    
    // Time indicators
    if (text.includes('today')) score += 3;
    if (text.includes('now')) score += 3;
    if (text.includes('asap')) score += 5;
    if (text.includes('immediately')) score += 5;
    
    return Math.min(score, 10); // Cap at 10
  }

  // Fallback analysis when AI is unavailable
  getFallbackAnalysis(title, description) {
    return {
      category: this.autoCategorize(title, description),
      priority: this.autoPrioritize(title, description),
      sentiment: this.analyzeSentiment(title, description),
      suggestedSolutions: this.generateSolutions(title, description),
      keywords: this.extractKeywords(title, description),
      urgency: this.calculateUrgency(title, description)
    };
  }

  // Generate smart response suggestions for admins
  async generateResponseSuggestions(ticket, context = '') {
    try {
      const priority = ticket.priority;
      
      const responses = {
        urgent: [
          'Thank you for bringing this urgent issue to our attention. We\'re working on it immediately and will have a solution for you shortly.',
          'We understand this is urgent and affecting your experience. Our team is prioritizing this issue and will resolve it as quickly as possible.',
          'This has been escalated to our highest priority. We\'ll provide updates as soon as we have more information.'
        ],
        high: [
          'Thank you for reporting this issue. We\'re investigating it now and will provide a solution shortly.',
          'We appreciate you bringing this to our attention. Our team is working on it and will update you soon.',
          'This is important to us. We\'re looking into it and will get back to you with a resolution.'
        ],
        medium: [
          'Thank you for your ticket. We\'re reviewing this issue and will respond with a solution soon.',
          'We\'ve received your request and are working on it. You\'ll hear from us shortly.',
          'Thanks for reaching out. We\'re on it and will provide an update soon.'
        ],
        low: [
          'Thank you for your suggestion. We\'ll review this and consider it for future updates.',
          'We appreciate your feedback. This has been noted for our development team.',
          'Thanks for sharing this idea. We\'ll evaluate it and let you know if we can implement it.'
        ]
      };
      
      return responses[priority] || responses.medium;
    } catch (error) {
      console.error('Response generation error:', error);
      return ['Thank you for your ticket. We\'ll get back to you soon.'];
    }
  }
}

// Export singleton instance
export const geminiAI = new GeminiAIService();

/**
 * Generate content using Gemini AI for moderation purposes
 * @param {string} prompt - The prompt to send to Gemini AI
 * @returns {Promise<string>} The AI-generated response
 */
export const generateContent = async (prompt) => {
  try {
    // For now, we'll use a mock implementation that simulates AI responses
    // In production, this would make actual API calls to Gemini AI
    
    // Simulate processing time
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mock AI response based on content analysis
    const content = prompt.toLowerCase();
    
    // Check for inappropriate content
    const inappropriateWords = ['profanity', 'hate', 'violence', 'adult', 'inappropriate'];
    const hasInappropriate = inappropriateWords.some(word => content.includes(word));
    
    // Check for copyright issues
    const copyrightWords = ['copyright', 'quote', 'lyrics', 'movie', 'book'];
    const hasCopyright = copyrightWords.some(word => content.includes(word));
    
    // Check for spam
    const spamWords = ['spam', 'repetitive', 'duplicate'];
    const hasSpam = spamWords.some(word => content.includes(word));
    
    // Check for quality issues
    const qualityWords = ['poor grammar', 'unclear', 'incorrect'];
    const hasQuality = qualityWords.some(word => content.includes(word));
    
    let category = 'safe';
    let severity = 'low';
    let approved = true;
    let issues = [];
    let recommendations = 'Content appears to be appropriate and well-formed.';
    let confidence = 0.95;
    
    if (hasInappropriate) {
      category = 'inappropriate';
      severity = 'high';
      approved = false;
      issues.push({
        type: 'inappropriate',
        description: 'Content contains inappropriate language or themes',
        suggestion: 'Please review and remove inappropriate content'
      });
      recommendations = 'Content contains inappropriate material that should be removed.';
      confidence = 0.9;
    } else if (hasCopyright) {
      category = 'copyright';
      severity = 'medium';
      approved = false;
      issues.push({
        type: 'copyright',
        description: 'Content may contain copyrighted material',
        suggestion: 'Please ensure you have permission to use this content'
      });
      recommendations = 'Content may contain copyrighted material. Please verify permissions.';
      confidence = 0.85;
    } else if (hasSpam) {
      category = 'spam';
      severity = 'medium';
      approved = false;
      issues.push({
        type: 'spam',
        description: 'Content appears to be spam or duplicate',
        suggestion: 'Please provide original, non-repetitive content'
      });
      recommendations = 'Content appears to be spam or duplicate. Please provide original content.';
      confidence = 0.8;
    } else if (hasQuality) {
      category = 'quality';
      severity = 'low';
      approved = false;
      issues.push({
        type: 'quality',
        description: 'Content has quality issues',
        suggestion: 'Please improve grammar, clarity, and accuracy'
      });
      recommendations = 'Content has quality issues. Please improve grammar and clarity.';
      confidence = 0.75;
    }
    
    // Return JSON response as expected by the moderation system
    return JSON.stringify({
      category,
      severity,
      approved,
      issues,
      recommendations,
      confidence
    });
    
  } catch (error) {
    console.error('Error generating content:', error);
    // Return fallback response
    return JSON.stringify({
      category: 'quality',
      severity: 'medium',
      approved: false,
      issues: [{
        type: 'quality',
        description: 'AI processing error',
        suggestion: 'Manual review required'
      }],
      recommendations: 'Content requires manual review due to processing error.',
      confidence: 0.5
    });
  }
};

/**
 * Generate chat responses for AI support
 * @param {string} prompt - The user's message
 * @param {string} userRole - The user's role (host, venue, admin, team)
 * @returns {Promise<string>} The AI-generated chat response
 */
export const generateChatResponse = async (prompt, userRole = null) => {
  try {
    // Simulate processing time
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const message = prompt.toLowerCase().trim();
    
    // FAQ Routing - Check for FAQ-related keywords first
    if (message.includes('faq') || message.includes('help') || message.includes('how to') || message.includes('guide') || message.includes('tutorial') || message.includes('documentation') || message.includes('manual') || message.includes('instructions') || 
        message.includes('revenue') || message.includes('earn') || message.includes('money') || message.includes('payment') || 
        message.includes('marketplace') || message.includes('buy') || message.includes('purchase') || message.includes('sell') || 
        message.includes('team') || message.includes('competition') || message.includes('analytics') || message.includes('tracking') || 
        message.includes('technical') || message.includes('error') || message.includes('bug') || message.includes('problem') || 
        message.includes('create') || message.includes('make') || message.includes('build') || message.includes('start') || 
        message.includes('account') || message.includes('sign up') || message.includes('register')) {
      return generateFAQResponse(message);
    }
    
    // Game creation and management - check this FIRST to avoid being caught by general help
    if (message.includes('create game') || message.includes('create a game') || message.includes('make game') || message.includes('new game') || message.includes('game creation') || message.includes('help me with game') || message.includes('start a game')) {
      return "To create a new trivia game, click <a href='/add-game' class='text-green-400 hover:text-green-300 underline'>Create Game</a> to get started. You can choose from different categories, set difficulty levels, and customize the game format. You'll also need to set a date, time, and venue for the game.";
    }
    
    // Check for general help requests (moved after specific requests)
    if (message.includes('what can you do') || message.includes('tell me what you can do') || message.includes('help me') || message.includes('assist')) {
      return "I'm here to help with Trivia Plug! I can assist with game creation, venue booking, technical support, platform features, billing, and hosting tips. What specific area would you like help with?";
    }
    
    // Venue booking
    if (message.includes('venue') || message.includes('location') || message.includes('book venue') || message.includes('find venue') || message.includes('venue booking')) {
      return "To book a venue, visit the <a href='/venue-discovery' class='text-green-400 hover:text-green-300 underline'>Venue Discovery</a> page to find available locations near you. You can filter by capacity, amenities, and availability. Once you find a venue you like, you can contact them directly or use our booking system if they're partnered with us.";
    }
    
    // Technical support
    if (message.includes('error') || message.includes('bug') || message.includes('not working') || message.includes('broken') || message.includes('technical') || message.includes('problem') || message.includes('issue')) {
      return "I'm sorry you're experiencing technical issues. Please try refreshing the page first. If the problem persists, you can create a support ticket through the <a href='/help-desk' class='text-green-400 hover:text-green-300 underline'>Help Desk</a>, or I can escalate this to our human support team for immediate assistance.";
    }
    
    // Platform features
    if (message.includes('feature') || message.includes('what can') || message.includes('how does') || message.includes('platform') || message.includes('capabilities') || message.includes('tell me what')) {
      return "Trivia Plug offers many features including <a href='/home' class='text-green-400 hover:text-green-300 underline'>game creation</a>, <a href='/team/profile' class='text-green-400 hover:text-green-300 underline'>team management</a>, real-time scoring, <a href='/venue-discovery' class='text-green-400 hover:text-green-300 underline'>venue booking</a>, <a href='/marketplace' class='text-green-400 hover:text-green-300 underline'>marketplace for games</a>, and <a href='/admin/analytics' class='text-green-400 hover:text-green-300 underline'>analytics</a>. You can also use our AI moderation for content review and access detailed performance reports.";
    }
    
    // Billing and payments
    if (message.includes('payment') || message.includes('billing') || message.includes('cost') || message.includes('price') || message.includes('subscription') || message.includes('plan') || message.includes('money')) {
      return "Our pricing varies based on your plan. Basic hosting is free, while premium features require a subscription. You can manage your billing information, payment methods, and subscription in your <a href='/account-settings' class='text-green-400 hover:text-green-300 underline'>Account Settings</a>. For specific pricing questions, please contact our <a href='/helpdesk' class='text-green-400 hover:text-green-300 underline'>support team</a>.";
    }
    
    // Hosting best practices
    if (message.includes('host') || message.includes('best practice') || message.includes('tips') || message.includes('advice') || message.includes('suggestions') || message.includes('recommendations')) {
      return "Great hosting tips: Start with a simple game format, engage with your audience, use clear questions, keep the pace moving, and always have backup questions ready. Remember to test your equipment beforehand and arrive early to set up. You can also check out our <a href='/helpdesk' class='text-green-400 hover:text-green-300 underline'>Help Desk</a> for more detailed guides and tutorials.";
    }
    
    // Booking-specific response
    if (message.includes('booking') || message.includes('venue booking') || message.includes('book a venue')) {
      return `
        <strong>Venue Booking Help</strong><br />
        To book a venue for your trivia game, go to the <a href="/schedule-game" class="text-green-400 underline">Schedule Game</a> page. There, you can select your preferred venue, date, and time, and complete your booking in a few easy steps.<br /><br />
        <a href="/schedule-game" class="text-green-400 underline">Click here to book a venue now</a>.<br /><br />
        If you need help with the process, let me know!
      `;
    }
    
    // Supplies and equipment
    if (message.includes('supplies') || message.includes('equipment') || message.includes('materials') || message.includes('gear') || message.includes('tools') || message.includes('accessories')) {
      return `
        <strong>Supplies & Equipment</strong><br />
        Visit our <a href="/supplies" class="text-green-400 underline">Supplies</a> page to browse trivia hosting essentials including buzzers, scoreboards, microphones, projectors, and more. We offer quality equipment for both small and large venues.<br /><br />
        <a href="/supplies" class="text-green-400 underline">Browse supplies now</a><br /><br />
        Need recommendations for your venue size? Let me know how many people you're hosting!
      `;
    }
    
    // Marketplace and game purchasing
    if (message.includes('buy') || message.includes('purchase') || message.includes('marketplace') || message.includes('shop') || message.includes('store') || message.includes('games for sale') || message.includes('buy games') || message.includes('purchase games') || message.includes('game store')) {
      return `
        <strong>Game Marketplace</strong><br />
        Visit our <a href="/marketplace" class="text-green-400 underline">Marketplace</a> to browse and purchase pre-made trivia games from other hosts and creators. You'll find games in various categories, difficulty levels, and themes - perfect for when you need a game quickly or want to try something new!<br /><br />
        <a href="/marketplace" class="text-green-400 underline">Browse the marketplace now</a><br /><br />
        All marketplace games are quality-checked and ready to use immediately after purchase.
      `;
    }
    
    // Profile management
    if (message.includes('profile') || message.includes('edit profile') || message.includes('update profile') || message.includes('change profile') || message.includes('profile settings') || message.includes('account settings') || message.includes('personal info') || message.includes('user profile')) {
      // Context-aware profile management based on user role
      if (userRole === 'host') {
        return `
          <strong>Host Profile Management</strong><br />
          As a host, you can manage your profile in these areas:<br /><br />
          • <a href="/profile" class="text-green-400 underline">Host Profile</a> - Edit your personal information, bio, and hosting preferences<br />
          • <a href="/account-settings" class="text-green-400 underline">Account Settings</a> - Update billing info, payment methods, and security settings<br /><br />
          <a href="/profile" class="text-green-400 underline">Go to your Host Profile</a> to edit your personal information.
        `;
      } else if (userRole === 'venue') {
        return `
          <strong>Venue Profile Management</strong><br />
          As a venue, you can manage your profile in these areas:<br /><br />
          • <a href="/account-settings" class="text-green-400 underline">Account Settings</a> - Update billing info, payment methods, and security settings<br />
          • <a href="/venue-discovery" class="text-green-400 underline">Venue Profile</a> - Update your venue information and availability<br /><br />
          <a href="/account-settings" class="text-green-400 underline">Go to Account Settings</a>
        `;
      } else if (userRole === 'admin') {
        return `
          <strong>Admin Profile Management</strong><br />
          As an admin, you can manage your profile in these areas:<br /><br />
          • <a href="/account-settings" class="text-green-400 underline">Account Settings</a> - Update billing info, payment methods, and security settings<br />
          • <a href="/admin" class="text-green-400 underline">Admin Dashboard</a> - Access admin tools and system management<br /><br />
          <a href="/account-settings" class="text-green-400 underline">Go to Account Settings</a>
        `;
      } else if (userRole === 'team') {
        return `
          <strong>Team Profile Management</strong><br />
          As a team member, you can manage your profile in these areas:<br /><br />
          • <a href="/team/profile" class="text-green-400 underline">Team Profile</a> - Edit your team information, members, and preferences<br />
          • <a href="/account-settings" class="text-green-400 underline">Account Settings</a> - Update personal account settings<br /><br />
          <a href="/team/profile" class="text-green-400 underline">Go to Team Profile</a>
        `;
      } else {
        // Fallback for unknown roles
        return `
          <strong>Profile Management</strong><br />
          You can edit your profile and account settings in several places:<br /><br />
          • <a href="/account-settings" class="text-green-400 underline">Account Settings</a> - Update billing info, payment methods, and security settings<br />
          • <a href="/home" class="text-green-400 underline">Host Dashboard</a> - Update your hosting profile and preferences<br />
          • <a href="/team/profile" class="text-green-400 underline">Team Profile</a> - Edit your team information, members, and preferences<br /><br />
          Which type of profile would you like to edit?
        `;
      }
    }
    
    // Default response
    return "I'm here to help with Trivia Plug! I can assist with <a href='/home' class='text-green-400 hover:text-green-300 underline'>game creation</a>, <a href='/venue-discovery' class='text-green-400 hover:text-green-300 underline'>venue booking</a>, <a href='/helpdesk' class='text-green-400 hover:text-green-300 underline'>technical support</a>, platform features, billing, and hosting tips. What specific area would you like help with?";
  } catch (error) {
    console.error('Error generating chat response:', error);
    return "I'm having trouble processing your request right now. Please try again or contact our support team directly.";
  }
};

/**
 * Generate FAQ-specific responses with intelligent routing
 * @param {string} message - The user's message
 * @returns {string} The FAQ response with relevant links
 */
const generateFAQResponse = (message) => {
  // Payment System
  if (message.includes('payment') || message.includes('money') || message.includes('cost') || message.includes('price') || message.includes('billing') || message.includes('subscription') || message.includes('plan') || message.includes('buy') || message.includes('purchase')) {
    return `
      <strong>Payment System</strong><br />
      I found relevant information about payments in our FAQ!<br /><br />
      <a href="/faq#payment-system" class="text-green-400 underline">📖 View Payment FAQ</a><br /><br />
      Quick answers about:
      • Payment methods
      • Pricing and costs
      • Revenue sharing
      • Billing and subscriptions
      • Game purchases
    `;
  }
  
  // Revenue Sharing
  if (message.includes('revenue') || message.includes('earn') || message.includes('money') || message.includes('profit') || message.includes('income') || message.includes('sales') || message.includes('marketplace') || message.includes('sell') || message.includes('creator') || message.includes('50/50') || message.includes('split')) {
    return `
      <strong>Revenue Sharing & Marketplace</strong><br />
      I found detailed information about revenue sharing in our FAQ!<br /><br />
      <a href="/faq#revenue-sharing" class="text-green-400 underline">📖 View Revenue Sharing FAQ</a><br /><br />
      Quick answers about:
      • How 50/50 revenue sharing works
      • Game purchases and pricing
      • Marketplace vs personal games
      • Revenue tracking and payments
      • Creator earnings
    `;
  }
  
  // Game Creation
  if (message.includes('create') || message.includes('make') || message.includes('build') || message.includes('design') || message.includes('write') || message.includes('question') || message.includes('trivia') || message.includes('game') || message.includes('content') || message.includes('submit')) {
    return `
      <strong>Game Creation & Content</strong><br />
      I found comprehensive guides about game creation in our FAQ!<br /><br />
      <a href="/faq#game-creation" class="text-green-400 underline">📖 View Game Creation FAQ</a><br /><br />
      Quick answers about:
      • How to create trivia games
      • Game structure and format
      • Quality guidelines
      • Personal vs marketplace games
      • Submission process
    `;
  }
  
  // Marketplace
  if (message.includes('marketplace') || message.includes('buy') || message.includes('purchase') || message.includes('rent') || message.includes('shop') || message.includes('store') || message.includes('approval') || message.includes('review') || message.includes('quality')) {
    return `
      <strong>Marketplace & Game Purchases</strong><br />
      I found detailed information about the marketplace in our FAQ!<br /><br />
      <a href="/faq#marketplace" class="text-green-400 underline">📖 View Marketplace FAQ</a><br /><br />
      Quick answers about:
      • How marketplace works
      • Buying vs renting games
      • Approval process
      • Quality control
      • Marketplace rules
    `;
  }
  
  // Teams and Competition
  if (message.includes('team') || message.includes('member') || message.includes('captain') || message.includes('competition') || message.includes('tournament') || message.includes('leaderboard') || message.includes('score') || message.includes('rank') || message.includes('join team') || message.includes('create team')) {
    return `
      <strong>Teams & Competition</strong><br />
      I found information about teams and competitions in our FAQ!<br /><br />
      <a href="/faq#teams" class="text-green-400 underline">📖 View Teams & Competition FAQ</a><br /><br />
      Quick answers about:
      • Creating and managing teams
      • Team competitions
      • Scoring systems
      • Leaderboards
      • Team roles and permissions
    `;
  }
  
  // Analytics and Tracking
  if (message.includes('analytics') || message.includes('tracking') || message.includes('stats') || message.includes('performance') || message.includes('data') || message.includes('report') || message.includes('metrics') || message.includes('earnings') || message.includes('sales data') || message.includes('statistics')) {
    return `
      <strong>Analytics & Tracking</strong><br />
      I found information about analytics and tracking in our FAQ!<br /><br />
      <a href="/faq#analytics" class="text-green-400 underline">📖 View Analytics & Tracking FAQ</a><br /><br />
      Quick answers about:
      • Available analytics
      • How tracking works
      • Revenue analytics
      • Performance metrics
      • Data access and export
    `;
  }
  
  // Technical Support
  if (message.includes('technical') || message.includes('error') || message.includes('bug') || message.includes('problem') || message.includes('issue') || message.includes('broken') || message.includes('not working') || message.includes('support') || message.includes('help') || message.includes('troubleshoot')) {
    return `
      <strong>Technical Support</strong><br />
      I found technical support information in our FAQ!<br /><br />
      <a href="/faq#technical" class="text-green-400 underline">📖 View Technical Support FAQ</a><br /><br />
      Quick answers about:
      • Browser compatibility
      • Account security
      • Common issues
      • Getting support
      • Troubleshooting guides
    `;
  }
  
  // Getting Started
  if (message.includes('start') || message.includes('begin') || message.includes('first time') || message.includes('new user') || message.includes('sign up') || message.includes('register') || message.includes('account') || message.includes('setup') || message.includes('onboarding')) {
    return `
      <strong>Getting Started</strong><br />
      I found getting started information in our FAQ!<br /><br />
      <a href="/faq#getting-started" class="text-green-400 underline">📖 View Getting Started FAQ</a><br /><br />
      Quick answers about:
      • What is Trivia Plug
      • How to sign up
      • Account types
      • Getting started steps
      • Platform overview
    `;
  }
  
  // General FAQ response
  return `
    <strong>FAQ & Help Center</strong><br />
    I can help you find specific information in our comprehensive FAQ!<br /><br />
    <a href="/faq" class="text-green-400 underline">📖 Browse All FAQ Categories</a><br /><br />
    <strong>Popular FAQ Categories:</strong><br />
    • <a href="/faq#getting-started" class="text-green-400 underline">🚀 Getting Started</a><br />
          • <a href="/faq#payment-system" class="text-green-400 underline">💰 Payments</a><br />
    • <a href="/faq#game-creation" class="text-green-400 underline">🎮 Game Creation</a><br />
    • <a href="/faq#marketplace" class="text-green-400 underline">🏪 Marketplace</a><br />
    • <a href="/faq#revenue-sharing" class="text-green-400 underline">💸 Revenue Sharing</a><br />
    • <a href="/faq#teams" class="text-green-400 underline">🏆 Teams & Competition</a><br />
    • <a href="/faq#analytics" class="text-green-400 underline">📊 Analytics & Tracking</a><br />
    • <a href="/faq#technical" class="text-green-400 underline">🔧 Technical Support</a><br /><br />
    What specific topic would you like to learn more about?
  `;
}; 